import React, { useEffect, useState } from 'react';
import {
  saveM365TenantId,
  dnsSyncPreview,
  dnsSyncWriteServices,
  dnsSyncWriteVerify,
} from '../api';

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Service-group filter. Matches supportedService values Microsoft Graph returns.
const SERVICE_OPTIONS = [
  { key: 'Email', label: 'Email (MX, SPF, autodiscover)', default: true },
  { key: 'OfficeCommunicationsOnline', label: 'Teams / Skype (SRV + CNAMEs)', default: true },
  { key: 'Intune', label: 'Intune MDM (device enrollment)', default: false },
  { key: 'SharepointDefaultDomain', label: 'SharePoint apex (⚠ may break your website)', default: false },
  { key: 'OrgIdAuthentication', label: 'Microsoft auth CNAME', default: false },
];

function ActionBadge({ action, willSkip, skipReason, error }) {
  if (error) return <span className="badge badge-red" title={error}>error</span>;
  if (willSkip || (skipReason && !action)) return <span className="badge badge-gray" title={skipReason}>will skip</span>;
  if (action === 'created') return <span className="badge badge-green">created</span>;
  if (action === 'updated') return <span className="badge badge-yellow">updated</span>;
  if (action === 'unchanged') return <span className="badge badge-gray">unchanged</span>;
  return <span className="badge badge-blue">pending</span>;
}

function RecordTable({ rows, title, countLabel = 'records' }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="table-card" style={{ marginBottom: 16 }}>
      <div className="table-header">
        <span className="table-title">{title}</span>
        <span className="table-count">{rows.length} {countLabel}</span>
      </div>
      <table>
        <thead>
          <tr>
            <th>Type</th>
            <th>Host</th>
            <th>Value</th>
            <th>TTL</th>
            <th>Pri/Wt/Port</th>
            <th>Service</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={r.willSkip || r.skipReason ? { opacity: 0.55 } : {}}>
              <td style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)' }}>{r.type || '—'}</td>
              <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{r.host || '@'}</td>
              <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text2)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.value || ''}>
                {r.value || '—'}
              </td>
              <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{r.ttl || '—'}</td>
              <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>
                {r.type === 'MX' && r.distance != null ? `p:${r.distance}` : ''}
                {r.type === 'SRV' ? `p:${r.distance ?? 0} w:${r.weight ?? 0} :${r.port ?? 0}` : ''}
              </td>
              <td style={{ fontSize: 11, color: 'var(--text3)' }}>{r.supportedService || '—'}</td>
              <td>
                <ActionBadge action={r.action} willSkip={r.willSkip} skipReason={r.skipReason || r.reason} error={r.error} />
                {(r.skipReason || r.reason) && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, maxWidth: 220 }}>{r.skipReason || r.reason}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DnsSyncModal({
  accountId,
  domain: initialDomain,
  initialTenantId = '',
  onClose,
  onTenantSaved,
}) {
  // Step: 'input' | 'preview' | 'writing' | 'result'
  const [step, setStep] = useState('input');
  const [domain, setDomain] = useState(initialDomain || '');
  const [tenantId, setTenantId] = useState(initialTenantId);
  const [persistTenant, setPersistTenant] = useState(!initialTenantId);

  const [services, setServices] = useState(
    SERVICE_OPTIONS.filter(s => s.default).map(s => s.key)
  );
  const [writeVerification, setWriteVerification] = useState(false); // optional phase 1

  const [preview, setPreview] = useState(null);   // { verification: [...], services: [...], tenantId }
  const [result, setResult] = useState(null);     // { verification?: ..., services: {...} }
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const tenantValid = GUID_RE.test(tenantId);
  const domainValid = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63})+$/.test(domain);

  function toggleService(key) {
    setServices(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }

  async function handlePreview() {
    setError('');
    setLoading(true);
    try {
      if (persistTenant) {
        await saveM365TenantId(accountId, tenantId);
        onTenantSaved?.(tenantId);
      }
      const data = await dnsSyncPreview(accountId, domain, { tenantId });
      setPreview(data);
      setStep('preview');
    } catch (e) {
      setError(formatError(e));
    }
    setLoading(false);
  }

  async function handleWrite() {
    setError('');
    setLoading(true);
    setStep('writing');
    try {
      let verificationResult = null;
      if (writeVerification) {
        verificationResult = await dnsSyncWriteVerify(accountId, { domain, tenantId });
      }
      const servicesResult = await dnsSyncWriteServices(accountId, {
        domain,
        tenantId,
        services,
      });
      setResult({ verification: verificationResult, services: servicesResult });
      setStep('result');
    } catch (e) {
      setError(formatError(e));
      setStep('preview'); // let them retry
    }
    setLoading(false);
  }

  function formatError(e) {
    if (e.status === 424) {
      return e.body?.hint
        ? `${e.message} — ${e.body.hint}`
        : `Microsoft Graph rejected the request (${e.message}). Check GDAP + Entra app permissions.`;
    }
    return e.message || 'Unknown error';
  }

  // Filter preview records by selected services for the "will-be-written" count.
  const filteredPreviewServices = (preview?.services || []).filter(r =>
    !services.length || services.includes(r.supportedService)
  );

  return (
    <div className="modal-backdrop" style={backdropStyle} onClick={onClose}>
      <div className="modal-content" style={contentStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Set up M365 DNS</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, fontFamily: 'var(--mono)' }}>
              {step === 'input'    && 'Step 1 of 3 — confirm tenant + domain'}
              {step === 'preview'  && 'Step 2 of 3 — review what will be written'}
              {step === 'writing'  && 'Writing records to NameSilo…'}
              {step === 'result'   && 'Step 3 of 3 — results'}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={bodyStyle}>
          {error && (
            <div className="error-state" style={{ marginBottom: 16 }}>⚠ {error}</div>
          )}

          {/* ── Step 1: inputs ── */}
          {step === 'input' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label style={labelStyle}>
                <span>Domain to configure</span>
                <input
                  value={domain}
                  onChange={e => setDomain(e.target.value)}
                  placeholder="example.com"
                  style={inputStyle}
                  autoFocus
                />
                {domain && !domainValid && <span style={hintErr}>Not a valid domain</span>}
              </label>

              <label style={labelStyle}>
                <span>Microsoft 365 Tenant ID (GUID)</span>
                <input
                  value={tenantId}
                  onChange={e => setTenantId(e.target.value.trim())}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  style={{ ...inputStyle, fontFamily: 'var(--mono)' }}
                />
                <span style={hint}>
                  Find it in Entra admin center → Overview → Tenant ID.
                  {initialTenantId && ' Pre-filled from this account.'}
                </span>
                {tenantId && !tenantValid && <span style={hintErr}>Not a valid GUID</span>}
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text2)' }}>
                <input
                  type="checkbox"
                  checked={persistTenant}
                  onChange={e => setPersistTenant(e.target.checked)}
                  disabled={!tenantValid || tenantId === initialTenantId}
                />
                Save this tenant ID on the account for next time
                {tenantId === initialTenantId && <span style={{ color: 'var(--text3)' }}>(already saved)</span>}
              </label>

              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>
                  Which Microsoft services to set up?
                </div>
                {SERVICE_OPTIONS.map(s => (
                  <label key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text2)', padding: '3px 0' }}>
                    <input
                      type="checkbox"
                      checked={services.includes(s.key)}
                      onChange={() => toggleService(s.key)}
                    />
                    {s.label}
                  </label>
                ))}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                <input
                  type="checkbox"
                  checked={writeVerification}
                  onChange={e => setWriteVerification(e.target.checked)}
                />
                Also write the Microsoft verification TXT record first
                <span style={{ color: 'var(--text3)' }}>(only needed for brand-new, unverified domains)</span>
              </label>
            </div>
          )}

          {/* ── Step 2: preview ── */}
          {step === 'preview' && preview && (
            <>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
                Reviewing <code style={codeStyle}>{preview.domain}</code> on tenant <code style={codeStyle}>{preview.tenantId}</code>.
                Records greyed out will not be written (either unsafe, or filtered by your service selection).
              </div>
              <RecordTable title="Verification records" rows={preview.verification} />
              <RecordTable
                title="Service records"
                rows={(preview.services || []).map(r => ({
                  ...r,
                  willSkip: r.willSkip || !services.includes(r.supportedService),
                  skipReason: r.skipReason || (!services.includes(r.supportedService) ? `Service "${r.supportedService}" not selected` : undefined),
                }))}
              />
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                {filteredPreviewServices.length} service record(s) will be written to NameSilo.
              </div>
            </>
          )}

          {/* ── Step: writing ── */}
          {step === 'writing' && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>
              <div className="loading-state" style={{ marginBottom: 12 }}>
                <span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" />
              </div>
              Writing DNS records to NameSilo…
            </div>
          )}

          {/* ── Step 3: result ── */}
          {step === 'result' && result && (
            <>
              {result.verification && (
                <RecordTable title="Verification results" rows={result.verification.records || []} />
              )}
              <RecordTable title="Service record results" rows={result.services?.records || []} />
              <div style={successBannerStyle}>
                ✓ DNS sync complete. Records are live on NameSilo; Microsoft-side propagation can take a few minutes.
              </div>
            </>
          )}
        </div>

        <div style={footerStyle}>
          {step === 'input' && (
            <>
              <button onClick={onClose} style={btnSecondary}>Cancel</button>
              <button
                onClick={handlePreview}
                disabled={!tenantValid || !domainValid || loading}
                style={btnPrimary}
              >
                {loading ? 'Loading…' : 'Preview →'}
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button onClick={() => setStep('input')} style={btnSecondary}>← Back</button>
              <button
                onClick={handleWrite}
                disabled={loading || filteredPreviewServices.length === 0}
                style={btnPrimary}
              >
                Confirm & write {filteredPreviewServices.length} record(s)
              </button>
            </>
          )}
          {step === 'writing' && (
            <button disabled style={btnSecondary}>Please wait…</button>
          )}
          {step === 'result' && (
            <button onClick={onClose} style={btnPrimary}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── styles (inline to avoid touching App.css — matches existing conventions) ─
const backdropStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const contentStyle = { width: '100%', maxWidth: 900, maxHeight: '90vh', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 20px', borderBottom: '1px solid var(--border)' };
const bodyStyle = { padding: 20, overflowY: 'auto', flex: 1 };
const footerStyle = { padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 };
const closeBtn = { background: 'none', border: 'none', color: 'var(--text3)', fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: 0 };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text2)' };
const inputStyle = { padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)' };
const hint = { fontSize: 11, color: 'var(--text3)', fontWeight: 400 };
const hintErr = { fontSize: 11, color: '#e55', fontWeight: 400 };
const btnPrimary = { padding: '8px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { padding: '8px 18px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const codeStyle = { fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', background: 'var(--bg)', padding: '2px 6px', borderRadius: 4 };
const successBannerStyle = { padding: 12, background: 'rgba(72,187,120,0.1)', border: '1px solid rgba(72,187,120,0.3)', borderRadius: 6, color: 'var(--text)', fontSize: 13, marginTop: 12 };
